import { useState, useMemo, useEffect } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Sparkles, ShieldCheck, Tag, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import AddonCard from "@/components/AddonCard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import CustomizeSkeleton from "@/components/CustomizeSkeleton";
import PriceSummary from "@/components/PriceSummary";
import EmbeddedCheckoutForm from "@/components/EmbeddedCheckoutForm";
import AnimatedPrice from "@/components/AnimatedPrice";
import {
  ADD_ONS,
  CORE_PRICE_ID,
  CORE_PRICE_CENTS,
  STACKS,
  getRecommendedAddons,
  formatPrice,
  type PersonalizationTag,
} from "@/lib/personalization";
import { getReferralData, getUtmData } from "@/hooks/useReferral";

const CustomizePlan = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [promoCode, setPromoCode] = useState("");
  const [promoStatus, setPromoStatus] = useState<"idle" | "valid" | "invalid" | "checking">("idle");
  const { ref: stacksRef, isVisible: stacksVisible } = useScrollReveal<HTMLDivElement>();

  useEffect(() => {
    const checkAssessment = async () => {
      if (!user) {
        navigate("/login");
        return;
      }
      const { data } = await supabase
        .from("health_assessments")
        .select("id")
        .eq("user_id", user.id)
        .limit(1);
      if (!data || data.length === 0) {
        toast({
          title: "Assessment Required",
          description: "Please complete your health assessment before choosing a plan.",
          variant: "destructive",
        });
        navigate("/get-started");
        return;
      }
      setIsLoading(false);
    };
    checkAssessment();
  }, [user, navigate, toast]);
  // Parse tags from URL
  const urlTags = useMemo(() => {
    const raw = searchParams.get("tags") || "";
    return raw
      .split(",")
      .map((t) => t.trim().toUpperCase())
      .filter((t) => ["METABOLIC", "ENERGY", "RECOVERY", "HORMONAL"].includes(t)) as PersonalizationTag[];
  }, [searchParams]);

  const recommended = useMemo(() => getRecommendedAddons(urlTags), [urlTags]);

  // Track selected add-ons
  const [selected, setSelected] = useState<Set<PersonalizationTag>>(() => {
    const initial = new Set<PersonalizationTag>();
    recommended.forEach((a) => {
      if (a.recommended) initial.add(a.id);
    });
    return initial;
  });

  const toggle = (id: PersonalizationTag) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyStack = (addonIds: PersonalizationTag[]) => {
    setSelected(new Set(addonIds));
  };

  const selectedAddons = ADD_ONS.filter((a) => selected.has(a.id));
  const totalCents = CORE_PRICE_CENTS + selectedAddons.reduce((sum, a) => sum + a.priceCents, 0);

  const handleConfirmCheckout = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setConfirmOpen(true);
  };

  const handleCheckout = async () => {
    // Fire confetti
    const confetti = (await import("canvas-confetti")).default;
    confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });

    setConfirmOpen(false);
    setShowCheckout(true);
  };

  // Prepare UTM/referral data for embedded checkout
  const referral = useMemo(() => getReferralData(), []);
  const utm = useMemo(() => getUtmData(), []);

  const checkoutPriceIds = useMemo(
    () => [CORE_PRICE_ID, ...selectedAddons.map((a) => a.priceId)],
    [selectedAddons]
  );

  const checkoutUtm = useMemo(
    () => (utm.utm_source ? utm : undefined),
    [utm]
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {isLoading ? (
        <CustomizeSkeleton />
      ) : showCheckout ? (
        /* Embedded Stripe Checkout — full-page takeover */
        <div className="container mx-auto px-4 pt-24 pb-16 max-w-2xl">
          <button
            onClick={() => setShowCheckout(false)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft size={16} /> Back to plan
          </button>

          <div className="text-center mb-8">
            <h1 className="font-display text-2xl md:text-3xl text-foreground mb-2">
              Complete Your Payment
            </h1>
            <p className="text-muted-foreground text-sm">
              {formatPrice(totalCents)}/mo. Your subscription can be modified or cancelled anytime.
            </p>
          </div>

          <EmbeddedCheckoutForm
            priceIds={checkoutPriceIds}
            mode="subscription"
            tags={Array.from(selected)}
            salespersonId={referral.salespersonId || undefined}
            commissionRate={referral.commissionRate}
            utm={checkoutUtm}
            promoCode={promoStatus === "valid" ? promoCode.trim().toUpperCase() : undefined}
          />
        </div>
      ) : (
        <>
      <div className="container mx-auto px-4 pt-24 pb-32 lg:pb-16">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="text-center max-w-2xl mx-auto mb-6">
          <p className="text-sm font-semibold uppercase tracking-widest text-champagne mb-2">
            Customize Your Plan
          </p>
          <h1 className="font-display text-3xl md:text-4xl text-foreground mb-3">
            Customize Your Plan
          </h1>
          <p className="text-muted-foreground">
            Enhance your treatment with additional therapies based on your needs and provider recommendations.
          </p>
        </div>

        {/* Next-steps context banner */}
        <div className="max-w-5xl mx-auto mb-8">
          <div className="flex items-start gap-3 p-4 rounded-xl border border-primary/20 bg-primary/5">
            <Info size={18} className="text-primary mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">You can subscribe now.</span> Your provider will review your assessment in the background (typically 24–48 hours). You'll only be charged for treatments after your provider approves them.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_340px] gap-8 max-w-5xl mx-auto items-start">
          {/* Add-on cards */}
          <div className="space-y-8">
            {/* Core — always included */}
            <div className="animate-fade-in">
              <Card className="border-primary/40 bg-primary/5">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-display text-lg text-foreground">
                        Core Membership
                      </h3>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
                        Included
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Provider consultations, treatment plans, prescription
                      management & ongoing support
                    </p>
                  </div>
                  <span className="font-display text-2xl text-foreground whitespace-nowrap ml-4">
                    {formatPrice(CORE_PRICE_CENTS)}
                    <span className="text-sm text-muted-foreground font-sans">
                      /mo
                    </span>
                  </span>
                </CardContent>
              </Card>
            </div>

            {/* Personalization hint */}
            {urlTags.length > 0 && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Sparkles size={14} className="text-primary" />
                Patients with similar profiles often choose:
              </p>
            )}

            {/* Add-on toggle cards */}
            <div className="space-y-4">
              {recommended.map((addon, index) => (
                <AddonCard
                  key={addon.id}
                  addon={addon}
                  isSelected={selected.has(addon.id)}
                  onToggle={() => toggle(addon.id)}
                  index={index}
                />
              ))}
            </div>

            {/* Stack Builder */}
            <div
              ref={stacksRef}
              className={`transition-all duration-500 ease-out ${
                stacksVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
            >
              <h3 className="font-display text-lg text-foreground mb-1">
                Popular Personalized Protocols
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Quick presets — select a protocol to auto-configure your add-ons.
              </p>
              <div className="grid sm:grid-cols-3 gap-3">
                {STACKS.map((stack, index) => {
                  const isActive =
                    stack.addons.length === selected.size &&
                    stack.addons.every((a) => selected.has(a));

                  return (
                    <button
                      key={stack.name}
                      type="button"
                      onClick={() => applyStack(stack.addons)}
                      className={`text-left p-4 rounded-xl border transition-all duration-200 hover:scale-[1.02] ${
                        stacksVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                      } ${
                        isActive
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/30"
                      }`}
                      style={{ transitionDelay: `${200 + index * 100}ms` }}
                    >
                      <p className="font-semibold text-sm text-foreground">
                        {stack.name}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {stack.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Compliance */}
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              All treatments require provider evaluation and approval.
              Results vary. These statements have not been evaluated by the
              FDA. This is not intended to diagnose, treat, cure, or prevent
              any disease.
            </p>
          </div>

          {/* Desktop sticky price summary */}
          <div className="hidden lg:block sticky top-24">
            <PriceSummary
              selectedAddons={selectedAddons}
              totalCents={totalCents}
              loading={loading}
              onCheckout={handleConfirmCheckout}
            />
          </div>
        </div>
      </div>

      {/* Mobile sticky bottom bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] z-50">
        <div className="flex items-center justify-between max-w-xl mx-auto">
          <div>
            <p className="text-xs text-muted-foreground">Monthly total</p>
            <p className="font-display text-2xl text-foreground">
              <AnimatedPrice value={formatPrice(totalCents)}>
                <span className="text-sm text-muted-foreground font-sans">
                  /mo
                </span>
              </AnimatedPrice>
            </p>
          </div>
          <Button onClick={handleConfirmCheckout} disabled={loading} className="px-8">
            {loading ? "Loading…" : "Continue"}
          </Button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <ShieldCheck size={20} className="text-primary" />
              Confirm Your Plan
            </DialogTitle>
            <DialogDescription>
              Review your selections before proceeding to checkout.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="flex justify-between text-sm">
              <span className="text-foreground font-medium">Core Membership</span>
              <span className="text-foreground">{formatPrice(CORE_PRICE_CENTS)}/mo</span>
            </div>

            {selectedAddons.map((a) => (
              <div key={a.id} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{a.name}</span>
                <span className="text-muted-foreground">+{formatPrice(a.priceCents)}/mo</span>
              </div>
            ))}

            {selectedAddons.length === 0 && (
              <p className="text-xs text-muted-foreground italic">
                Core membership only. No add-ons selected.
              </p>
            )}

            <div className="border-t border-border pt-3 flex justify-between items-baseline">
              <span className="font-display text-foreground">Monthly Total</span>
              <AnimatedPrice value={formatPrice(totalCents)} className="font-display text-2xl text-foreground">
                <span className="text-sm text-muted-foreground font-sans">/mo</span>
              </AnimatedPrice>
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Your payment will be processed securely. Your subscription can be modified or cancelled anytime.
          </p>

          {/* Promo code input */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
              <Tag size={14} className="text-muted-foreground" />
              Have a promo code?
            </label>
            <div className="flex gap-2">
              <Input
                placeholder="e.g. SARAH20"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""));
                  setPromoStatus("idle");
                }}
                className="uppercase font-mono"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!promoCode.trim() || promoStatus === "checking"}
                onClick={async () => {
                  setPromoStatus("checking");
                  const { data, error } = await supabase
                    .from("promo_codes")
                    .select("id")
                    .ilike("code", promoCode.trim())
                    .eq("is_active", true)
                    .limit(1);
                  if (error || !data || data.length === 0) {
                    setPromoStatus("invalid");
                  } else {
                    setPromoStatus("valid");
                  }
                }}
              >
                {promoStatus === "checking" ? "…" : "Apply"}
              </Button>
            </div>
            {promoStatus === "valid" && (
              <p className="text-xs text-emerald-600 font-medium">✓ Promo code applied</p>
            )}
            {promoStatus === "invalid" && (
              <p className="text-xs text-destructive">Invalid or expired promo code</p>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Go Back
            </Button>
            <Button onClick={handleCheckout} disabled={loading}>
              {loading ? "Loading…" : "Proceed to Checkout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
};

export default CustomizePlan;
